// How Panther measures each venue, in the words the methodology page shows.
// Written from the collector (workers/collector: Robinhood market job,
// transfer scanner, venue engine) and the venue readers in packages/venues.
// The capNote / volumeNote text for the issuing platforms is
// copied from packages/venues/src/platforms/info.ts (that package is not a
// dependency of the web app); keep the two in step when a reader changes.

/** When this page was last checked against the code it describes. */
export const METHODOLOGY_REVIEWED_ON = "2026-10-08";

/** Collector cadence: cron at :00 and :30 UTC. */
export const REFRESH_MINUTES = 30;

/** Oldest quote used for any value (packages/core robinhood.ts, packages/venues freshness.ts). */
export const PRICE_MAX_AGE_HOURS = 96;
/** Share of a token's supply that must be covered before mint + burn is published (feed.ts). */
export const COVERAGE_SHARE = 0.9;

/** Which Nasdaq trade a listed share price is (the collector's regular-session rule). */
export const LISTED_PRICE_SESSION =
  "Listed share prices (Nasdaq) are taken from the regular session only (9:30 am–4:00 pm US Eastern): the latest regular-session trade while the market is open, and the official closing price from the close until the next open. Pre-market and after-hours trades are never used.";

export type PriceBasis = "issuer" | "oracle" | "market" | "underlying" | "exchange" | "nav";

export const PRICE_BASES: Record<PriceBasis, { label: string; meaning: string }> = {
  issuer: { label: "Issuer quote", meaning: "A price the issuer itself publishes for the token." },
  oracle: { label: "Oracle", meaning: "An on-chain price feed maintained by an oracle network (Chainlink)." },
  market: { label: "Market", meaning: "The token's own market price: read from a deep on-chain pool where one agrees with DefiLlama, else as DefiLlama reports it (else CoinGecko)." },
  underlying: {
    label: "Underlying share",
    meaning:
      `The listed share's price (Nasdaq) times the shares one token stands for. Used only when no market price is available and the share multiplier is known. ${LISTED_PRICE_SESSION}`,
  },
  exchange: {
    label: "Listed share",
    meaning: `The listed stock's price (Nasdaq), for tokens that are the registered shares themselves. ${LISTED_PRICE_SESSION}`,
  },
  nav: { label: "Fund NAV", meaning: "A fund's official net asset value per share (Nasdaq), published once a day." },
};

/** Where the data comes from: the methodology page lists these, the site footer credits the linked ones. */
export const SOURCES: Array<{ name: string; url: string | null; use: string }> = [
  {
    name: "On-chain reads",
    url: null,
    use: "Every token supply, the largest stablecoins' supply on their main chains, and the prices of tokens priced by an oracle or vault contract, read each run from the chains themselves through public RPC endpoints and node providers.",
  },
  {
    name: "Issuers",
    url: null,
    use: "Token lists, quotes, NAVs, yields and reserve reports published by the issuers and venues: Robinhood, xStocks, Ondo, Spiko, STOKR, Etherfuse, Franklin Templeton and others.",
  },
  { name: "Chainlink", url: "https://chain.link", use: "Price feeds for fund NAVs, gold and silver, and EUR and GBP exchange rates." },
  {
    name: "DefiLlama",
    url: "https://defillama.com",
    use: "The stablecoin list, prices, exchange rates and history, and the supply of every stablecoin and chain Panther does not read on chain itself (for the ones it does, DefiLlama's figure is kept beside ours as a cross-check); token prices by contract address, used first wherever DefiLlama has a confident, fresh price; protocol TVL for platforms valued as one row; and price and TVL history for the long-run charts.",
  },
  {
    name: "CoinGecko",
    url: "https://www.coingecko.com",
    use: "Prices of the tokens DefiLlama does not price, 24h trading volumes, and listings used to find new tokens.",
  },
  {
    name: "Nasdaq",
    url: "https://www.nasdaq.com",
    use: "Prices of the listed shares and funds some tokens are priced from, and end-of-day prices for insider trades.",
  },
  { name: "ECB", url: "https://www.ecb.europa.eu", use: "Euro reference exchange rates, to value products priced in other currencies in USD." },
  { name: "New York Fed", url: "https://www.newyorkfed.org", use: "SOFR, the benchmark class yields are compared with." },
  {
    name: "SEC EDGAR",
    url: "https://www.sec.gov/search-filings",
    use: "Money market funds' N-MFP filings, for their yields, and Form 4 filings for insider trades.",
  },
];

export interface VenueMethod {
  /** Venue id, as in the API (`/v1/venues/{id}/board`). */
  id: string;
  label: string;
  /** Chains read for supply. */
  chains: string;
  /** Price bases used, in order of preference. */
  bases: PriceBasis[];
  /** How the token set is found. */
  tokens: string;
  /** What the price is. */
  price: string;
  /** What the supply figure is. */
  supply: string;
  /** How market cap is computed. */
  cap: string;
  /** How 24h mint + burn is measured. */
  flow: string;
  /** Short form of `flow` for the summary table. */
  flowShort: string;
  /** Extra metrics only this venue has. */
  extra?: Array<{ term: string; text: string }>;
}

export const VENUE_METHODS: VenueMethod[] = [
  {
    id: "robinhood",
    label: "Robinhood",
    chains: "Robinhood Chain",
    bases: ["issuer"],
    tokens:
      "Robinhood's public token catalog: every asset with a deployment on Robinhood Chain. A token that leaves the catalog stops appearing on the board but stays in history.",
    price:
      "Midpoint of Robinhood's token bid and ask. Where only the per-share bid and ask are given, each is multiplied by the token's current share multiplier, once. A quote that is not in USD, is more than 96 hours old, is dated in the future, or predates a multiplier change that has since taken effect is not used.",
    supply: "Each contract's totalSupply on Robinhood Chain, read in one Multicall3 batch at a single block.",
    cap: "supply × price",
    flow: "USD minted + burned today, as Robinhood reports it in its quote feed. Robinhood's daily reset time is not published, so this window may differ from a rolling 24 hours.",
    flowShort: "Robinhood-reported, today",
    extra: [
      { term: "Trading volume", text: "Robinhood-reported daily trading volume, USD." },
      { term: "Trading halt", text: "Robinhood's trading-halt flag on the quote." },
    ],
  },
  {
    id: "coinbase",
    label: "Coinbase",
    chains: "Base",
    bases: ["oracle", "underlying"],
    tokens:
      "Coinbase's Tokenized Stocks API, each token checked on chain. Listed tokens that were never minted are left out.",
    price: "The token's Chainlink price feed on Base; else Coinbase's published NAV; else the listed share price times the token's on-chain share multiplier.",
    supply: "Each contract's totalSupply on Base, read in Multicall3 batches with the price feeds.",
    cap: "supply on Base × price",
    flow: "Tokens minted + burned over a rolling 24 hours, from a scan of mint and burn Transfer logs on Base, valued in USD.",
    flowShort: "On-chain scan, rolling 24h",
  },
  {
    id: "ondo",
    label: "Ondo",
    chains: "Ethereum, BNB Chain, and Solana",
    bases: ["market", "underlying", "issuer"],
    tokens:
      "Ondo's published token list. Solana mints are matched through CoinGecko's listing of the same Ethereum contract and accepted only when the mint account shows it is Ondo's. Cash and treasury tokens on the same list are left out.",
    price:
      "Ondo's own posted price from its on-chain oracles while it is current (under an hour old in market hours; after the close, only within 2% of the market); otherwise the market price (DefiLlama, else CoinGecko); otherwise the listed share price times Ondo's share multiplier.",
    supply: "Supply on each chain, in tokens, summed. Copies bridged to further chains are not counted again.",
    cap: "supply on Ethereum + BNB Chain + Solana × price, summed. On BNB Chain about 13% of the cap (30–75% of the BNB supply of CRCLon, MSTRon, GOOGLon and other large tokens) sits in Binance Alpha's custody wallet: tokens Binance users bought, counted like any exchange holding, not Ondo inventory",
    flow: "Tokens minted + burned, last 24h, USD (Ethereum + BNB Chain + Solana).",
    flowShort: "On-chain scan, 3 chains",
  },
  {
    id: "xstocks",
    label: "xStocks",
    chains: "Solana, Ethereum, BNB Chain, and 8 more",
    bases: ["market", "issuer"],
    tokens:
      "The issuer's token catalog, checked against every page of its proof-of-reserves feed. Tokens the reserves feed shows with zero circulating supply are left out.",
    price:
      "The token's Raydium or Orca pool against USDC where it is deep (at least $25k to move it 2%) and within 2% of the market quote; else the market price (DefiLlama, else CoinGecko), only where the token's contract shows one share per token; otherwise the issuer's own per-share quote.",
    supply:
      "The issuer's proof-of-reserves circulating supply, in shares, counted once. Per-chain supplies are not added up, because bridged copies would count the same shares twice.",
    cap: "issuer's circulating supply × price, summed",
    flow: "Tokens minted + burned, last 24h, USD (Solana + Ethereum + BNB Chain).",
    flowShort: "On-chain scan, 3 chains",
  },
  {
    id: "bstocks",
    label: "bStocks",
    chains: "BNB Chain",
    bases: ["market", "underlying"],
    tokens:
      "CoinGecko listings named as bStocks tokens with a BNB Chain contract. The issuer publishes no machine-readable token list.",
    price: "The token's PancakeSwap or Uniswap pool against USDT or USDC where it is deep (at least $25k to move it 2%) and within 2% of the market price; else the market price (DefiLlama, else CoinGecko); otherwise the listed share price (one share per token).",
    supply: "On-chain supply on BNB Chain.",
    cap: "on-chain total supply × price. Most tokens sit in Binance exchange wallets that also hold customer balances, so some may be unsold inventory; no public source separates the two",
    flow: "Tokens minted + burned, last 24h, USD (BNB Chain).",
    flowShort: "On-chain scan",
  },
  {
    id: "securitize",
    label: "Securitize",
    chains: "Avalanche, Solana, and Ethereum",
    bases: ["exchange"],
    tokens: "A short list of contracts checked on chain, including Exodus shares issued on Algorand.",
    price: "The listed stock's last sale (Nasdaq): these tokens are the registered shares themselves.",
    supply:
      "Supply on each chain, summed. On Algorand, the asset's total less what its reserve account still holds.",
    cap: "supply on every chain × listed share price (Nasdaq), summed. SECZ counts every tokenized share on chain, including shares held under the post-merger lock-up (to about 2026-12-28), so its free float is smaller; it is not the full register (163.3M shares outstanding)",
    flow: "Tokens minted + burned, last 24h, USD (Avalanche, Ethereum, Solana; not Algorand).",
    flowShort: "On-chain scan, not Algorand",
  },
  {
    id: "dinari",
    label: "Dinari",
    chains: "Arbitrum, Ethereum, Base, and Avalanche",
    bases: ["market", "underlying"],
    tokens:
      "Dinari's published factory contracts, plus tokens from its creating contract that are verified against Dinari's own beacon. Until every source has been read, CoinGecko listings stand in.",
    price: "Market price (DefiLlama, else CoinGecko); otherwise the listed share price (one share per token).",
    supply: "On-chain supply on each chain, summed.",
    cap: "supply on four chains × market price (DefiLlama, else CoinGecko), summed",
    flow: "Tokens minted + burned, last 24h, USD (Arbitrum, Ethereum, Base, Avalanche).",
    flowShort: "On-chain scan, 4 chains",
  },
  {
    id: "figure",
    label: "Figure",
    chains: "Provenance",
    bases: ["market"],
    tokens: "Figure's stock denomination on Provenance.",
    price: "Market price (DefiLlama, else CoinGecko).",
    supply: "The denomination's supply on Provenance.",
    cap: "Provenance denom supply × CoinGecko price",
    flow: "Tokens minted + burned, last 24h, USD (Provenance).",
    flowShort: "On-chain scan",
  },
  {
    id: "reality",
    label: "Reality",
    chains: "Arbitrum and Morph",
    bases: ["market", "underlying"],
    tokens:
      "Reality's minting registry on each chain: every token it registered, still accepts, and that names the registry as its minter. Until every registry has been read, CoinGecko listings stand in.",
    price: "Market price (DefiLlama, else CoinGecko); otherwise the listed share price (one share per token).",
    supply: "On-chain supply on each chain, summed.",
    cap: "supply on Arbitrum + Morph × market price (DefiLlama, else CoinGecko), summed",
    flow: "Tokens minted + burned, last 24h, USD (Arbitrum + Morph).",
    flowShort: "On-chain scan, 2 chains",
  },
  {
    id: "openingbell",
    label: "Opening Bell",
    chains: "Solana",
    bases: ["exchange"],
    tokens: "Superstate's registered share issues on Solana, listed individually and checked on chain.",
    price: "The listed stock's last sale (Nasdaq): these tokens are the registered shares themselves.",
    supply: "Supply of each Solana mint.",
    cap: "Solana supply × listed share price (Nasdaq), summed",
    flow: "Tokens minted + burned, last 24h, USD (Solana).",
    flowShort: "On-chain scan",
  },
  {
    id: "wisdomtree",
    label: "WisdomTree",
    chains: "Stellar",
    bases: ["nav"],
    tokens:
      "WisdomTree's equity Digital Funds on Stellar, each pinned to an issuer account WisdomTree publishes. Copies on other chains that WisdomTree does not tie to itself are left out.",
    price: "The fund's official daily NAV (Nasdaq), which carries a date but no time of day.",
    supply: "Fund shares issued on Stellar.",
    cap: "fund shares on Stellar × official daily NAV (Nasdaq), summed",
    flow: "Tokens minted + burned, last 24h, USD (Stellar).",
    flowShort: "On-chain scan",
  },
];
